/**
 * Клиентские хелперы прав Диска: эффективный режим папки вычисляется по
 * плоскому списку папок (access явный или у корня прав access_root_id).
 * UI-подсказки; сервер authoritative (init-фильтр видимости, rules, setAccess).
 *
 * Для custom-зон точность гейтов даёт виртуальная колонка `my_acl_level`
 * (чейн `withMyAclLevel()` в useDriveFolders): эффективный ACL-уровень
 * текущего юзера в зоне. Без неё custom-ветки консервативно закрыты.
 */
import type { Folder } from '../../../schemas/folder.schema';

export type DriveAccess = 'private' | 'public_read' | 'public_write' | 'custom';

/**
 * Порядок уровней для монотонного сужения: private < public_read < public_write.
 * custom приравнен к private (самый узкий) — сужать в него можно из любой зоны.
 */
export const DRIVE_ACCESS_LEVEL: Record<DriveAccess, number> = {
  private: 0,
  custom: 0,
  public_read: 1,
  public_write: 2,
};

export function isDriveAccess(value: unknown): value is DriveAccess {
  return (
    value === 'private' ||
    value === 'public_read' ||
    value === 'public_write' ||
    value === 'custom'
  );
}

/** Подписи режимов (селектор, бейджи, значки дерева). */
export const DRIVE_ACCESS_LABEL: Record<DriveAccess, string> = {
  private: 'Личная',
  custom: 'Настраиваемая',
  public_read: 'Только чтение',
  public_write: 'Всем авторизованным',
};

/** Уровни ACL custom-зоны (клиентское зеркало серверной карты, кумулятивно). */
export const DRIVE_ACL_LEVEL = {
  read: 0,
  add: 1,
  delete: 2,
  admin: 3,
} as const;

export type DriveAclLevel = keyof typeof DRIVE_ACL_LEVEL;

export function isDriveAclLevel(value: unknown): value is DriveAclLevel {
  return value === 'read' || value === 'add' || value === 'delete' || value === 'admin';
}

/** Короткое имя уровня — сам по себе, без включённых (бейджи, компактные места). */
export const DRIVE_ACL_LEVEL_LABEL: Record<DriveAclLevel, string> = {
  read: 'Чтение',
  add: 'Добавление',
  delete: 'Удаление',
  admin: 'Администрирование',
};

/**
 * Подпись уровня СО ВСЕМ, что он включает («Удаление, Добавление, Чтение») —
 * кумулятивность видна прямо в тексте. Единый источник для селектора формы
 * «Доступ…» и тултипов аватарок: расходиться им нельзя, это одно и то же право.
 * `admin` перечисления не имеет — он про настройку зоны, а не про объекты.
 */
export const DRIVE_ACL_LEVEL_FULL_LABEL: Record<DriveAclLevel, string> = {
  read: 'Чтение',
  add: 'Добавление, Чтение',
  delete: 'Удаление, Добавление, Чтение',
  admin: 'Администрирование',
};

/** Опции селектора уровня (форма «Доступ…») — от того же источника подписей. */
export const DRIVE_ACL_LEVEL_OPTIONS: Array<{ value: DriveAclLevel; label: string }> = (
  ['read', 'add', 'delete', 'admin'] as const
).map(level => ({ value: level, label: DRIVE_ACL_LEVEL_FULL_LABEL[level] }));

/** Описания уровней для формы (кумулятивность и append-only — важно показать). */
export const DRIVE_ACL_LEVEL_DESCRIPTION: Record<DriveAclLevel, string> = {
  read: 'Видит папку, читает и скачивает файлы',
  add: '+ создаёт папки и загружает файлы (без правки и удаления — даже своего)',
  delete: '+ переименовывает, перемещает и удаляет любые объекты зоны',
  admin: '+ настраивает доступ и переименовывает саму зону',
};

/** Строка папки с виртуальной колонкой `withMyAclLevel()` (может отсутствовать). */
export type DriveFolderRow = Folder & { my_acl_level?: number | null };

/** Эффективный режим папки: свой access или режим корня прав. */
export interface EffectiveAccess {
  access: DriveAccess;
  /** Папка-корень прав (владелец зоны = её автор). */
  rootId: number;
  ownerId: number | null;
  /** Режим задан на самой папке (не унаследован). */
  explicit: boolean;
  /** Мой ACL-уровень в custom-зоне (число DRIVE_ACL_LEVEL; null — не участник/нет данных). */
  myAclLevel: number | null;
}

export function getEffectiveAccess(
  folder: DriveFolderRow,
  foldersById: Map<number, DriveFolderRow>
): EffectiveAccess | null {
  if (isDriveAccess(folder.access)) {
    return {
      access: folder.access,
      rootId: folder.id,
      ownerId: folder.created_by_id ?? null,
      explicit: true,
      myAclLevel: folder.my_acl_level ?? null,
    };
  }
  if (folder.access_root_id == null) return null;
  const root = foldersById.get(folder.access_root_id);
  if (!root || !isDriveAccess(root.access)) return null;
  return {
    access: root.access,
    rootId: root.id,
    ownerId: root.created_by_id ?? null,
    explicit: false,
    // Уровень одинаков для всей зоны — берём с любой её строки
    myAclLevel: folder.my_acl_level ?? root.my_acl_level ?? null,
  };
}

/**
 * Цвет иконки папки: личная зона (эффективно private) — grape, в тон замочку;
 * настраиваемая (custom) — indigo; остальные — фирменный жёлтый.
 */
export function folderIconColor(eff: EffectiveAccess | null): string {
  if (eff?.access === 'private') return 'var(--mantine-color-grape-6)';
  if (eff?.access === 'custom') return 'var(--mantine-color-indigo-6)';
  return 'var(--mantine-color-yellow-9)';
}

/** Право писать в папку (создавать папки, загружать файлы). */
export function canWriteFolder(
  eff: EffectiveAccess | null,
  userId: number | null,
  isAdmin: boolean
): boolean {
  if (isAdmin) return true;
  if (!eff) return true; // нет данных о зоне — не блокируем, сервер authoritative
  if (eff.access === 'custom') {
    return (
      (userId != null && eff.ownerId === userId) ||
      (eff.myAclLevel != null && eff.myAclLevel >= DRIVE_ACL_LEVEL.add)
    );
  }
  return eff.access === 'public_write' || (userId != null && eff.ownerId === userId);
}

/**
 * Право менять/удалять папку по матрице зоны:
 * public_write — автор объекта; public_read/private — владелец зоны;
 * custom — владелец зоны или delete+ (append-only не правит даже своё), а для
 * САМОГО корня зоны — владелец или admin-уровень (администрирование).
 */
export function canModifyFolder(
  folder: DriveFolderRow,
  eff: EffectiveAccess | null,
  userId: number | null,
  isAdmin: boolean
): boolean {
  if (isAdmin) return true;
  if (userId == null) return false;
  if (eff?.access === 'custom') {
    if (eff.ownerId === userId) return true;
    const required = folder.id === eff.rootId ? DRIVE_ACL_LEVEL.admin : DRIVE_ACL_LEVEL.delete;
    return eff.myAclLevel != null && eff.myAclLevel >= required;
  }
  if (!eff || eff.access === 'public_write') return folder.created_by_id === userId;
  return eff.ownerId === userId;
}

/**
 * Право открыть «Доступ…» (настройка режима/ACL папки):
 * - внутри custom-зоны у НЕ-корня явные режимы запрещены (листовость) — пункт
 *   закрыт для всех, включая ADMIN (сервер откажет);
 * - корень custom-зоны — владелец / admin-уровень / ADMIN;
 * - остальные режимы — как canModifyFolder (автор/владелец зоны/ADMIN).
 */
export function canChangeFolderAccess(
  folder: DriveFolderRow,
  eff: EffectiveAccess | null,
  userId: number | null,
  isAdmin: boolean
): boolean {
  if (eff?.access === 'custom' && folder.id !== eff.rootId) return false; // листовость
  if (isAdmin) return true;
  if (userId == null) return false;
  if (eff?.access === 'custom') {
    return (
      eff.ownerId === userId ||
      (eff.myAclLevel != null && eff.myAclLevel >= DRIVE_ACL_LEVEL.admin)
    );
  }
  return canModifyFolder(folder, eff, userId, isAdmin);
}
